import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { slugify } from '../../utils/helpers';
import { ChevronRight, PhoneCall, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TreatmentDetail() {
  const { id } = useParams();
  const [treatment, setTreatment] = useState(null);
  const [allTreatments, setAllTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/treatments`).then(res => {
      setAllTreatments(res.data);
      const found = res.data.find(t => t.id === id || slugify(t.title) === id);
      setTreatment(found);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center">Yükleniyor...</div>;
  
  if (!treatment) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center p-6 text-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Tedavi bulunamadı.</h2>
          <a href="/#tedaviler" className="text-primary hover:underline font-medium">Tüm Tedavilere Dön</a>
        </div>
      </main>
      <Footer />
    </div>
  );

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  return (
    <div className="bg-slate-50 min-h-screen flex flex-col">
      <Navbar />
      
      {/* HEADER BREADCRUMB */}
      <div className="bg-slate-900 py-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white mb-2">{treatment.title}</h1>
          <div className="flex items-center text-sm text-slate-400 gap-2">
            <a href="/" className="hover:text-primary transition-colors">Ana Sayfa</a>
            <ChevronRight className="w-4 h-4" />
            <a href="/#tedaviler" className="hover:text-primary transition-colors">Tedavilerimiz</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-200">{treatment.title}</span>
          </div>
        </div>
      </div>

      <main className="flex-1 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          
          {/* LEFT CONTENT AREA */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
              <div className="aspect-[16/9] mb-8 rounded-2xl overflow-hidden shadow-sm">
                <img src={getImageUrl(treatment.image)} alt={treatment.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">{treatment.title} Nedir?</h2>
              <div className="prose prose-slate prose-lg max-w-none prose-headings:font-heading prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-primary prose-strong:text-slate-900">
                {treatment.content?.split('\n').map((para, i) => (
                  para.trim() ? <p key={i} className="mb-6 leading-relaxed">{para}</p> : null
                ))}
              </div>
            </div>
            
            <div className="bg-primary/5 p-8 rounded-3xl border border-primary/20 text-center">
              <h3 className="text-2xl font-bold font-heading text-slate-900 mb-4">Randevu Almak İster misiniz?</h3>
              <p className="text-slate-600 mb-6">Uzman hekimlerimizden randevu almak için hemen bizimle iletişime geçin.</p>
              <a href={`https://wa.me/905524818100?text=${encodeURIComponent(`Merhaba, ${treatment.title} tedavisi ile ilgili randevu oluşturmak istiyorum.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#20bd5a] transition-colors shadow-lg">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.305-.885-.653-1.482-1.46-1.656-1.758-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01a1.054 1.054 0 0 0-.762.356c-.261.284-1.025 1.002-1.025 2.443s1.05 2.836 1.198 3.034c.149.198 2.065 3.155 5.002 4.423.699.303 1.245.484 1.67.62.702.223 1.34.191 1.84.116.564-.085 1.758-.718 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                Randevu Oluştur
              </a>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:col-span-1 space-y-8">
            
            {/* Sidebar Widget 1: Treatments List */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold font-heading text-slate-900 mb-6 pb-4 border-b border-slate-100">Tüm Tedavilerimiz</h3>
              <ul className="space-y-2">
                {allTreatments.map(t => {
                  const isActive = slugify(t.title) === slugify(treatment.title);
                  return (
                    <li key={slugify(t.title)}>
                      <a 
                        href={`/tedaviler/${slugify(t.title)}`}
                        className={`flex items-center justify-between p-3 rounded-xl transition-all font-medium ${isActive ? 'bg-primary text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-primary'}`}
                      >
                        {t.title}
                        <ChevronRight className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Sidebar Widget 2: CTA Appointment */}
            <div className="bg-slate-900 rounded-3xl p-8 text-center relative overflow-hidden group">
              <div className="absolute inset-0 bg-primary/20 group-hover:bg-primary/30 transition-colors"></div>
              <div className="relative z-10">
                <a href={`https://wa.me/905524818100?text=${encodeURIComponent(`Merhaba, ${treatment.title} tedavisi ile ilgili randevu oluşturmak istiyorum.`)}`} target="_blank" rel="noreferrer" className="block w-full bg-[#25D366] text-white py-4 rounded-xl font-bold hover:bg-[#20bd5a] transition-colors shadow-lg flex items-center justify-center gap-2">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.305-.885-.653-1.482-1.46-1.656-1.758-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01a1.054 1.054 0 0 0-.762.356c-.261.284-1.025 1.002-1.025 2.443s1.05 2.836 1.198 3.034c.149.198 2.065 3.155 5.002 4.423.699.303 1.245.484 1.67.62.702.223 1.34.191 1.84.116.564-.085 1.758-.718 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                  Randevu Oluştur
                </a>
              </div>
            </div>

          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
